"use client";
import { useEffect, useState, type FormEvent } from "react";
import { commerceRequest } from "./CheckoutForm";

function useLinkToken() {
  const [token, setToken] = useState("");
  useEffect(() => {
    // Fragments stay out of request logs, referrers and server analytics.
    setToken(window.location.hash.slice(1));
  }, []);
  return token;
}
export function RecoveryForm() {
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState("");
  const [error, setError] = useState("");
  async function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (busy) return;
    const email = new FormData(e.currentTarget).get("email");
    setBusy(true);
    setError("");
    setNotice("");
    try {
      await commerceRequest("recover", { email });
      setNotice(
        "If a paid purchase matches that address, we’ll email a fresh download link. Check your inbox and spam folder.",
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : "Please try again.");
    } finally {
      setBusy(false);
    }
  }
  return (
    <form onSubmit={submit} aria-busy={busy}>
      <h2>Find your purchase.</h2>
      <p className="co-fine">
        Use the email you entered at checkout. Your product remains available
        even if you unsubscribe from updates.
      </p>
      <label htmlFor="recovery-email">Purchase email</label>
      <input
        id="recovery-email"
        name="email"
        type="email"
        autoComplete="email"
        required
        maxLength={254}
        disabled={busy}
      />
      <button className="co-button" disabled={busy}>
        {busy ? "Please wait…" : "Email a fresh link ↗"}
      </button>
      {notice && (
        <p className="co-notice" role="status">
          {notice}
        </p>
      )}
      {error && (
        <p className="co-error" role="alert">
          {error}
        </p>
      )}
    </form>
  );
}
function Download({ token }: { token: string }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  async function download() {
    setBusy(true);
    setError("");
    try {
      const r = await fetch("/api/commerce/download", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token }),
      });
      if (!r.ok) throw new Error((await r.json()).error);
      const url = URL.createObjectURL(await r.blob());
      const a = document.createElement("a");
      a.href = url;
      a.download = "Haulbase-Freight-Operations-Playbook.zip";
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 30000);
    } catch (e) {
      setError(
        e instanceof Error
          ? e.message
          : "Unable to download. Try a fresh link below.",
      );
    } finally {
      setBusy(false);
    }
  }
  return (
    <div className="co-download">
      <p className="co-eyebrow">YOUR OPERATING KIT</p>
      <h2>The Freight Operations Playbook</h2>
      <p>
        Start with START-HERE, then open the field guide. Your editable SOPs,
        templates and spreadsheets are in the same ZIP.
      </p>
      <button
        className="co-button"
        onClick={() => void download()}
        disabled={busy}
      >
        {busy ? "Preparing download…" : "Download the complete kit ↓"}
      </button>
      {error && (
        <p className="co-error" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}
export function Receipt() {
  const [data, setData] = useState<{ status: string; token?: string } | null>(
    null,
  );
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  async function refresh() {
    setBusy(true);
    setError("");
    try {
      setData(await commerceRequest("receipt"));
    } catch {
      setError(
        "We couldn’t check your payment yet. Please try again or check your delivery email.",
      );
    } finally {
      setBusy(false);
    }
  }
  useEffect(() => {
    void refresh();
  }, []);
  return (
    <>
      <p className="co-eyebrow">YOUR NEXT MOVE STARTS HERE.</p>
      <h1>{data?.status === "paid" ? "Make it yours." : "Your purchase."}</h1>
      {data?.status === "paid" && data.token ? (
        <>
          <p>
            Payment confirmed. Your complete kit is ready below and will also
            arrive by email.
          </p>
          <Download token={data.token} />
        </>
      ) : (
        <div className="co-status">
          <p role="status">
            {!data
              ? "Checking your payment…"
              : data.status === "pending"
                ? "Your payment is still being confirmed. We’ll deliver your kit after it clears. You do not need to pay again."
                : data.status === "refunded"
                  ? "This purchase has been refunded."
                  : "We couldn’t find a purchase in this browser. Check your delivery email or request a fresh link below."}
          </p>
          <button
            className="co-button"
            disabled={busy}
            onClick={() => void refresh()}
          >
            {busy ? "Checking…" : "Check again"}
          </button>
        </div>
      )}
      {error && (
        <p className="co-error" role="alert">
          {error}
        </p>
      )}
      <RecoveryForm />
    </>
  );
}
export function PurchaseAccess() {
  const token = useLinkToken();
  const [valid, setValid] = useState(false);
  const [error, setError] = useState("");
  useEffect(() => {
    if (token)
      void commerceRequest("access", { token })
        .then(() => setValid(true))
        .catch(() =>
          setError(
            "This link has expired or the purchase is unavailable. Request a fresh link below.",
          ),
        );
  }, [token]);
  return (
    <>
      <p className="co-eyebrow">THE OPERATOR’S FIELD KIT</p>
      <h1>
        Your playbook.
        <br />
        <em>Ready when you are.</em>
      </h1>
      {valid && <Download token={token} />}
      <p className="co-fine">
        Your kit is delivered as a ZIP with the PDF field guide and editable
        resources.
      </p>
      {error && (
        <p className="co-error" role="alert">
          {error}
        </p>
      )}
      <RecoveryForm />
    </>
  );
}
export function PrivateApplication() {
  const token = useLinkToken();
  const [info, setInfo] = useState<{
    email: string;
    status: string | null;
  } | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  useEffect(() => {
    if (token)
      void commerceRequest("application", { token })
        .then(setInfo)
        .catch(() =>
          setError(
            "This invitation is invalid or expired. Contact jerrod.a@haulbase.ai for help.",
          ),
        );
  }, [token]);
  async function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setError("");
    const data = Object.fromEntries(new FormData(e.currentTarget));
    try {
      await commerceRequest("apply", { ...data, token });
      setInfo((old) => (old ? { ...old, status: "submitted" } : null));
    } catch (e) {
      setError(e instanceof Error ? e.message : "Please try again.");
    } finally {
      setBusy(false);
    }
  }
  return (
    <>
      <p className="co-eyebrow">PRIVATE ACCESS / BY APPLICATION</p>
      <h1>
        Bring us your
        <br />
        <em>next challenge.</em>
      </h1>
      <p>
        Haulbase is pre-launch. Tell us how your freight operation works. We
        review for workflow fit as we build the execution layer. Applying does
        not activate a workspace or guarantee a launch date.
      </p>
      {error && (
        <p className="co-error" role="alert">
          {error}
        </p>
      )}
      {!token && (
        <div className="co-status">
          <h2>Your invitation is personal.</h2>
          <p>
            Open the application link from your Haulbase email. If you haven’t
            received one, start with the{" "}
            <a href="/playbook">Freight Operations Playbook</a> and opt in to
            private-access invitations.
          </p>
        </div>
      )}
      {info?.status ? (
        <div className="co-status" role="status">
          <h2>
            {info.status === "submitted"
              ? "Application received."
              : info.status === "approved"
                ? "Accepted for an onboarding conversation."
                : info.status === "waitlisted"
                  ? "You’re on the waitlist."
                  : "Application reviewed."}
          </h2>
          <p>
            {info.status === "submitted"
              ? "We’ll review your operation and email you with the next step."
              : info.status === "approved"
                ? "We’ll coordinate your supported workflow and workspace setup personally. Access is not active yet."
                : "Check your email for the review outcome. Your purchased kit remains yours to use."}
          </p>
        </div>
      ) : (
        info && (
          <form onSubmit={submit} aria-busy={busy}>
            <p className="co-notice">
              This invitation belongs to {info.email}.
            </p>
            <div className="co-form-grid">
              {[
                ["name", "Your name"],
                ["company", "Company"],
                ["role", "Your role"],
                ["teamSize", "Operations team size"],
                ["volume", "Approximate loads per month"],
                ["modes", "Freight modes"],
                ["currentTools", "Current TMS / operating tools"],
              ].map(([name, label]) => (
                <div key={name}>
                  <label htmlFor={name}>{label}</label>
                  <input
                    id={name}
                    name={name}
                    maxLength={200}
                    required
                    disabled={busy}
                  />
                </div>
              ))}
              <div>
                <label htmlFor="readiness">When are you ready to start?</label>
                <select
                  id="readiness"
                  name="readiness"
                  required
                  disabled={busy}
                >
                  <option value="">Select a timeframe</option>
                  <option>Now</option>
                  <option>Within 90 days</option>
                  <option>Exploring options</option>
                </select>
              </div>
              <div className="co-wide">
                <label htmlFor="pain">What is slowing your team down?</label>
                <textarea
                  id="pain"
                  name="pain"
                  maxLength={1500}
                  required
                  disabled={busy}
                  placeholder="Describe one recurring problem you want to solve."
                />
              </div>
            </div>
            <p className="co-fine">
              We’ll use these details to review your application and contact you
              about it. <a href="/legal/privacy">Privacy policy</a>.
            </p>
            <button className="co-button" disabled={busy}>
              {busy ? "Submitting…" : "Submit your application ↗"}
            </button>
          </form>
        )
      )}
    </>
  );
}
export function EmailPreferences() {
  const token = useLinkToken();
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  return (
    <>
      <p className="co-eyebrow">YOUR INBOX. YOUR CHOICE.</p>
      <h1>Email preferences.</h1>
      <p>
        Stop workflow tips and private-access invitations. Purchase delivery and
        responses to your application are unaffected.
      </p>
      {done ? (
        <p className="co-notice" role="status">
          You’re unsubscribed from promotional emails.
        </p>
      ) : (
        <button
          className="co-button"
          disabled={!token || busy}
          onClick={async () => {
            setBusy(true);
            try {
              await commerceRequest("unsubscribe", { token });
              setDone(true);
            } catch {
              setError(
                "This link is invalid. Contact jerrod.a@haulbase.ai for help.",
              );
            } finally {
              setBusy(false);
            }
          }}
        >
          {busy ? "Saving…" : "Unsubscribe"}
        </button>
      )}
      {!token && <p>Open the preferences link from your email.</p>}
      {error && (
        <p className="co-error" role="alert">
          {error}
        </p>
      )}
    </>
  );
}
